import { Info } from '@mui/icons-material';
import {
    Box,
    Slider,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { type PlaneMm, planeOffsetMm } from '~/domain/geometry/patientGeometry';
import type { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { resolveTextureSlice } from '~/engine/data/structure/planeSliceResolution';
import { getSigmaForStrength } from '~/engine/util/algorithms/sdf/GaussianSmoothing';
import { applyGaussianSmoothingSigmaGL } from '~/engine/util/algorithms/sdf/GaussianSmoothingGL';
import { formatCm3, formatMmAsCmAtScale } from '~/utils/units';
import { AUTO_PREVIEW_DELAY_MS } from '~/components/toolbar/OperationsToolbar/autoPreview';
import OperationApplyButton from '~/components/toolbar/OperationsToolbar/OperationApplyButton';
import ShowPreviewToggle from '~/components/toolbar/OperationsToolbar/ShowPreviewToggle';
import { usePostProcessingPreview } from '~/hooks/structure-set/usePostProcessingPreview';
import { useRoiSdfOperation } from '~/hooks/structure-set/useRoiSdfOperation';
import { useShownSlicePlaneOrRefusal } from '~/hooks/structure-set/useShownSlicePlaneOrRefusal';

/**
 * Smoothing selects a set of blur AXES, not a connectivity rule, so it
 * carries its own three-way mode instead of the shared 2D/3D
 * {@link ModeToggle} the Fill and Parts tabs use:
 *
 * - `2D_ALL` — in-plane, every slice; no value ever crosses a slice
 *   boundary
 * - `2D_CURRENT` — in-plane, restricted to the acquired image slice a
 *   tile of the edit target shows; every other slice comes back
 *   byte-identical
 * - `3D` — blur across the volume; the field is reinitialized first so
 *   the Z pass sees metric distances
 *
 * Both 2D modes run the same engine path (`mode: '2D'`) and differ only
 * in whether a target slice index is passed.
 */
type SmoothingMode = '2D_ALL' | '2D_CURRENT' | '3D';

/**
 * 2D (all) is the default. Blurring along Z is what erodes a structure
 * spanning few slices disproportionately — each slice pulls towards its
 * neighbours — so the volumetric mode is opt-in rather than the path a
 * clinician lands on by default.
 *
 * This is NOT volume preservation: in-plane smoothing still shrinks
 * convex contours, it just drops the cross-slice component. The
 * before → after readout is shown in this mode precisely so the residual
 * change is verified rather than assumed away.
 */
const DEFAULT_SMOOTHING_MODE: SmoothingMode = '2D_ALL';

// Structure smoothing exposes a narrower travel than the engine's full
// strength scale: 1..5 maps to sigma 0.2..1.4 voxels, whose largest
// in-plane kernel (radius ceil(3 * 1.4) = 5 voxels) still fits inside
// the display SDF's metric falloff band — which is what lets 2D mode
// skip the reinitialize step (see applyGaussianSmoothingSigmaGL).
const MIN_SMOOTHING_STRENGTH = 1;
const MAX_SMOOTHING_STRENGTH = 5;
const DEFAULT_SMOOTHING_STRENGTH = 3;
// Slider quantization; also the grid the sigma mapping is deterministic
// over, so the same visible setting always reproduces the same result.
const SMOOTHING_STRENGTH_STEP = 0.1;

/**
 * The preview handoff key of a smoothing setting. Only the single-slice
 * mode's result depends on the slice, so only its key carries the plane,
 * by its offset along the normal rather than its point: panning or zooming
 * within one slice must not invalidate the preview it is being used to
 * judge.
 */
function smoothingPreviewKey(
    strength: number,
    mode: SmoothingMode,
    plane: PlaneMm | null
): string {
    return mode === '2D_CURRENT'
        ? `smoothing:${strength}:2D_CURRENT:${plane && planeOffsetMm(plane)}`
        : `smoothing:${strength}:${mode}`;
}

// ── Blur-axis mode toggle ────────────────────────────────────────────

/**
 * Three-way, and smoothing-only: see {@link SmoothingMode} for why this
 * is not the shared connectivity toggle. The explanation hangs off an
 * adjacent Info icon rather than wrapping the group, for the reason
 * ModeToggle states: a tooltip over the buttons hides what the click is
 * about to change.
 *
 * Rendered default first and 2D family first, as the shared ModeToggle
 * and every other mode toggle in the panel are.
 */
function SmoothingModeToggle({
    mode,
    onChange,
    disabled,
}: {
    mode: SmoothingMode;
    onChange: (mode: SmoothingMode) => void;
    disabled: boolean;
}) {
    const intl = useIntl();
    const handleChange = (
        _event: React.MouseEvent<HTMLElement>,
        value: SmoothingMode | null
    ) => {
        // ToggleButtonGroup emits null when the active button is re-clicked;
        // ignore it so a mode is always selected.
        if (value !== null) {
            onChange(value);
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={handleChange}
                size="small"
                disabled={disabled}
                aria-label={intl.formatMessage({
                    id: 'postProcessing.smooth.mode',
                })}
            >
                <ToggleButton value="2D_ALL">
                    <FormattedMessage id="postProcessing.smooth.mode.2dAll" />
                </ToggleButton>
                <ToggleButton value="2D_CURRENT">
                    <FormattedMessage id="postProcessing.smooth.mode.2dCurrent" />
                </ToggleButton>
                <ToggleButton value="3D">
                    <FormattedMessage id="postProcessing.smooth.mode.3d" />
                </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip
                title={
                    <FormattedMessage id="postProcessing.smooth.mode.tooltip" />
                }
                placement="right"
            >
                <Info
                    tabIndex={0}
                    role="button"
                    aria-label={intl.formatMessage({
                        id: 'postProcessing.smooth.mode.tooltip',
                    })}
                    sx={{ fontSize: 12, opacity: 0.6 }}
                />
            </Tooltip>
        </Box>
    );
}

/**
 * Signed percentage change between the live and previewed volume, or
 * an em dash when the reference volume is degenerate. Precision (one
 * decimal) matches the clinical relevance of a whole-structure change.
 */
function formatVolumeDeltaPercent(before: number, after: number): string {
    if (before <= 0) {
        return '—';
    }
    const deltaPercent = ((after - before) / before) * 100;
    const sign = deltaPercent > 0 ? '+' : '';
    return `${sign}${deltaPercent.toFixed(1)} %`;
}

// ── Smooth tab content ───────────────────────────────────────────────

export default function SmoothTab() {
    const intl = useIntl();
    const { isProcessing, executeOperation } = useRoiSdfOperation();
    const {
        isPreviewing,
        isPreviewReady,
        isPreviewVisible,
        setPreviewVisible,
        preview,
        consumePreviewedTexture,
        invalidatePreview,
        clearPreview,
        sdfRevision,
        previewVolumeCm3,
    } = usePostProcessingPreview();
    const { environment, roiId: selectedRoiId } = useActiveEditTarget();
    const { plane, readPlane, refusalMessageId, readPlaneOrRefuse } =
        useShownSlicePlaneOrRefusal('smoothAllSlices');
    const [smoothingStrength, setSmoothingStrength] = useState(
        DEFAULT_SMOOTHING_STRENGTH
    );
    const [mode, setMode] = useState<SmoothingMode>(DEFAULT_SMOOTHING_MODE);

    // Only the single-slice mode needs a slice to target, and so only it
    // needs a tile of the edit target showing its acquired slice. 2D (all)
    // smooths every slice of the structure's own grid and is available
    // whatever the tiles show.
    const isCurrentSliceMode = mode === '2D_CURRENT';
    // Centre of the image slice the shown plane cuts: the slice 2D
    // (current) smooths, whose position the readout states.
    const shownSliceCentre = plane
        ? environment?.image?.parent.grid.sliceCenterOnPlane(plane)
        : undefined;
    const is2dUnavailable =
        isCurrentSliceMode && shownSliceCentre === undefined;
    // Patient Z of the smoothed slice's centre, in cm.
    const slicePositionCm = shownSliceCentre
        ? formatMmAsCmAtScale(shownSliceCentre[2], 'macro')
        : '';

    const previewKey = smoothingPreviewKey(smoothingStrength, mode, plane);

    // Whether the before → after volume readout is meaningful. Both
    // whole-structure modes change the volume and must show it; a
    // single-slice change is below the readout's usefulness, so that mode
    // shows its target slice instead.
    const showsVolume = !isCurrentSliceMode;

    // Volume of the live structure for the before → after readout;
    // re-read after in-place edits and applies (sdfRevision).
    const beforeVolumeCm3 = useMemo((): number | undefined => {
        void sdfRevision;
        return selectedRoiId === null
            ? undefined
            : environment?.structureSet?.getRoi(selectedRoiId)?.volumeCm3;
    }, [environment, selectedRoiId, sdfRevision]);

    // The 2D target slice as a Z index of the given texture's grid, or
    // undefined when no tile shows the acquired slice (`slicePlane` null)
    // or the slice lies outside the structure's texture (nothing to smooth).
    const resolve2dSliceIndex = useCallback(
        (
            srcTexture: ContourTexture,
            slicePlane: PlaneMm | null
        ): number | undefined => {
            const image = environment?.image;
            if (slicePlane === null || !image) {
                return undefined;
            }
            const resolved = resolveTextureSlice(srcTexture, image, slicePlane);
            return resolved.kind === 'slice' ? resolved.sliceZ : undefined;
        },
        [environment]
    );

    // `slicePlane` is the shown slice 2D (current) smooths, read by the
    // caller at the moment it asks: a scroll between render and the compute
    // must target the slice the clinician is looking at then.
    const computeSmoothed = useCallback(
        async (
            srcTexture: ContourTexture,
            slicePlane: PlaneMm | null,
            signal?: AbortSignal
        ): Promise<ContourTexture | undefined> => {
            // The strength control is continuous, so the sigma is
            // resolved here rather than rounded onto the engine's
            // stepped level table.
            const sigma = getSigmaForStrength(smoothingStrength);
            if (mode === '2D_CURRENT') {
                const sliceIndex = resolve2dSliceIndex(srcTexture, slicePlane);
                if (sliceIndex === undefined) {
                    return undefined;
                }
                return applyGaussianSmoothingSigmaGL(srcTexture, sigma, {
                    mode: '2D',
                    signal,
                    slice2DIndex: sliceIndex,
                });
            }
            // 2D (all): the same in-plane path with no slice restriction,
            // so every slice is smoothed and none is blurred into another.
            return applyGaussianSmoothingSigmaGL(srcTexture, sigma, {
                mode: mode === '3D' ? '3D' : '2D',
                signal,
            });
        },
        [mode, smoothingStrength, resolve2dSliceIndex]
    );

    // Auto-preview: (re)compute the smoothed result, debounced, whenever
    // the strength, mode or (in 2D current) the viewed slice changes — or
    // the selected ROI is edited (sdfRevision).
    //
    // This tab previews on a debounce rather than on a button because its
    // parameter is a DRAGGED slider, as Crop's is a dragged box: a
    // Preview step here would read as drag, click, look, drag again. A
    // smoothing result is also the tab in this panel that most needs the
    // computed number — the result looks almost exactly like the
    // structure it replaces, and the volume readout below is what
    // separates them.
    //
    // invalidatePreview (not clearPreview) keeps the prior preview on
    // screen until the recompute swaps it in; Apply is gated on
    // readiness, so nothing can be committed against it meanwhile.
    // Skipped during an apply so the commit isn't disturbed. With no tile
    // showing the acquired slice in 2D (current) the preview is torn down
    // entirely so a stale result can't be mistaken for the current state.
    useEffect(() => {
        if (isProcessing) {
            return;
        }
        if (is2dUnavailable) {
            clearPreview();
            return;
        }
        invalidatePreview();
        const timeout = setTimeout(() => {
            preview(
                // Tagged at the call site rather than inside
                // computeSmoothed, which the apply path also uses and
                // which wants the texture itself. Smoothing always alters
                // the field, so a missing texture is a failure here, never
                // an 'unchanged' answer.
                async (srcTexture, signal) => {
                    const texture = await computeSmoothed(
                        srcTexture,
                        readPlane(),
                        signal
                    );
                    return texture
                        ? { status: 'changed', texture }
                        : { status: 'failed' };
                },
                previewKey,
                {
                    hidePreviewedStructure: true,
                    withVolume: showsVolume,
                }
            );
        }, AUTO_PREVIEW_DELAY_MS);
        return () => clearTimeout(timeout);
    }, [
        showsVolume,
        previewKey,
        is2dUnavailable,
        isProcessing,
        sdfRevision,
        computeSmoothed,
        readPlane,
        preview,
        invalidatePreview,
        clearPreview,
    ]);

    const handleSliderChange = (_event: Event, value: number | number[]) => {
        setSmoothingStrength(value as number);
    };

    const handleApply = () => {
        // Read at the click, never captured at render: the tile that showed
        // the slice may be gone or turned, or the slice scrolled; with none
        // shown the refusal is stated.
        const appliedPlane = isCurrentSliceMode ? readPlaneOrRefuse() : null;
        if (isCurrentSliceMode && appliedPlane === null) {
            return;
        }
        const appliedKey = smoothingPreviewKey(
            smoothingStrength,
            mode,
            appliedPlane
        );
        executeOperation({
            operationFn: async (roi) => {
                // Prefer the previewed texture — the commit is then exactly
                // what the clinician saw. Recompute only if the handoff
                // misses (e.g. superseded key, or a slice other than the
                // previewed one shown at the click).
                let texture = consumePreviewedTexture(appliedKey);
                if (!texture) {
                    clearPreview();
                    texture = await computeSmoothed(roi.texture!, appliedPlane);
                }
                if (!texture) {
                    return { status: 'failed' };
                }
                return { status: 'changed', texture };
            },
            // Named for the operation, not for the button: this string
            // lands verbatim in the ROI's undo-history entry and in the
            // audit trail, so it is kept byte-identical with what the
            // standalone smoothing panel wrote.
            operationName: 'Gaussian Smoothing',
            busyMessageId: 'postProcessing.smooth.busy',
            successMessageId: 'postProcessing.smooth.success',
            failedMessageId: 'postProcessing.smooth.failed',
            logLabel: 'Gaussian smoothing',
        });
    };

    const showVolumes =
        showsVolume &&
        isPreviewReady &&
        beforeVolumeCm3 !== undefined &&
        previewVolumeCm3 !== undefined;

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <SmoothingModeToggle
                mode={mode}
                onChange={setMode}
                disabled={isProcessing}
            />
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="postProcessing.smooth.description" />
            </Typography>
            {/* Continuous strength: the underlying sigma interpolates
                between the level table entries, so no discrete steps or
                numeric value are surfaced — the ends are labelled
                instead. Extra vertical padding widens the hit area. */}
            <Slider
                value={smoothingStrength}
                onChange={handleSliderChange}
                step={SMOOTHING_STRENGTH_STEP}
                min={MIN_SMOOTHING_STRENGTH}
                max={MAX_SMOOTHING_STRENGTH}
                disabled={isProcessing}
                sx={{ py: 1.75, mb: -1 }}
                aria-label={intl.formatMessage({
                    id: 'postProcessing.smooth.strength',
                })}
            />
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="postProcessing.smooth.light" />
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    aria-hidden
                >
                    →
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="postProcessing.smooth.strong" />
                </Typography>
            </Box>
            {/* Honest volume readout for both whole-structure modes:
                smoothing may change the volume — 2D (all) less than 3D,
                but never not at all — and the clinician verifies the
                effect here before applying. A single-slice change is
                below the readout's resolution, so that mode shows the
                target slice instead. */}
            {showsVolume && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                    sx={{
                        // The whole before → after → delta statement has
                        // to read as ONE line inside the panel: wrapped
                        // onto two, the "after" volume and its percentage
                        // separate from the "before" they are to be
                        // compared against. Tabular figures keep it from
                        // jittering as the live preview updates.
                        fontSize: 11,
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {showVolumes ? (
                        <FormattedMessage
                            id="postProcessing.smooth.volume"
                            values={{
                                before: formatCm3(beforeVolumeCm3),
                                after: formatCm3(previewVolumeCm3),
                                delta: formatVolumeDeltaPercent(
                                    beforeVolumeCm3,
                                    previewVolumeCm3
                                ),
                            }}
                        />
                    ) : (
                        <FormattedMessage id="postProcessing.smooth.volume.pending" />
                    )}
                </Typography>
            )}
            {/* Single-slice target indicator: which slice the apply will
                smooth, or why that mode is currently unavailable. */}
            {isCurrentSliceMode && (
                <Typography
                    variant="caption"
                    color={is2dUnavailable ? 'warning.main' : 'text.secondary'}
                    component="div"
                    // Sized with the volume readout above it: the two
                    // occupy the same slot depending on the mode, so a
                    // step in size between them would read as a change
                    // in importance rather than in content.
                    sx={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
                    data-testid="post-processing-smooth-slice"
                >
                    {is2dUnavailable ? (
                        <FormattedMessage id={refusalMessageId} />
                    ) : (
                        <FormattedMessage
                            id="postProcessing.smooth.slice2d"
                            values={{ position: slicePositionCm }}
                        />
                    )}
                </Typography>
            )}
            <ShowPreviewToggle
                visible={isPreviewVisible}
                onChange={setPreviewVisible}
                disabled={!isPreviewReady}
            />
            <OperationApplyButton
                onApply={handleApply}
                isPreviewing={isPreviewing}
                // Readiness costs the user nothing here: the preview is
                // automatic, so it arrives within the debounce rather
                // than waiting on a press. What it buys is the handoff —
                // Apply commits the very texture on screen via
                // consumePreviewedTexture, and a smoothing result is
                // indistinguishable by eye from the structure it
                // replaces, so what was previewed is the only account of
                // what was applied.
                isPreviewReady={isPreviewReady}
                isProcessing={isProcessing}
            />
        </Box>
    );
}
